import { useEffect, useState } from "react";
import { useImageFileSelection } from "../components/ImageAssetUploadFields";
import type { AssetResponse } from "../generated/api/types.gen";

/** The state every asset modal carries: which tab is showing, what has been
 *  picked or uploaded, and the reset that runs each time the modal opens so a
 *  second visit never inherits the first one's half-finished work. */
export function useAssetModalState<T extends string>(
  isOpen: boolean,
  defaultTab: T,
  /** Applied when the modal opens. A dropped image arrives already chosen,
   *  on the caller's tab, instead of an empty form. */
  seed?: { file: File | null; name: string },
) {
  const seedFile = seed?.file ?? null;
  const seedName = seed?.name ?? "";
  const [activeTab, setActiveTab] = useState<T>(defaultTab);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [previewAsset, setPreviewAsset] = useState<AssetResponse | null>(null);
  const [name, setName] = useState("");
  const [saveAsAsset, setSaveAsAsset] = useState(false);
  const { file, setFile, previewUrl } = useImageFileSelection(isOpen);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
      setSelectedAssetId(null);
      setPreviewAsset(null);
      setName(seedName);
      setSaveAsAsset(false);
      setFile(seedFile);
    }
  }, [isOpen, defaultTab, seedFile, seedName, setFile]);

  return {
    activeTab,
    setActiveTab,
    selectedAssetId,
    setSelectedAssetId,
    previewAsset,
    setPreviewAsset,
    name,
    setName,
    saveAsAsset,
    setSaveAsAsset,
    file,
    setFile,
    previewUrl,
  };
}
